import { useEffect, useState } from 'react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { BookOpen, MessageSquare, Users, FileText, Trophy, Clock, Calendar, Search, Eye, Globe } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { capitalizeFirst } from '@/lib/textUtils';
import { usePageVisit } from '@/hooks/usePageVisit';
import { getCurrentVersion } from '@/lib/changelog';

interface Stats {
  wordCount: number;
  clueCount: number;
  topicCount: number;
  replyCount: number;
  userCount: number;
  wordLengthStats: { length: string; count: number }[];
}

interface ForumLeader {
  author_name: string;
  post_count: number;
}

interface ContributionLeader {
  display_name: string;
  count: number;
}

interface RecentWord {
  id: number;
  word: string;
  created_at: string;
}

interface RecentClue {
  id: number;
  clue: string;
  word: string;
  created_at: string;
}

interface TimeStats {
  last24h: number;
  last7d: number;
  last30d: number;
  lastYear: number;
}

interface GrowthDataPoint {
  date: string;
  words: number;
  clues: number;
}

interface SearchStats {
  totalSearches: number;
  wordSearches: number;
  clueSearches: number;
  last24h: number;
  last7d: number;
  last30d: number;
}

interface SearchGrowthPoint {
  date: string;
  wordSearches: number;
  clueSearches: number;
}

interface VisitorDataPoint {
  date: string;
  visitors: number;
  pageViews: number;
}

interface LanguageGrowthPoint {
  date: string;
  Svenska: number;
  Engelska: number;
  Tyska: number;
  Franska: number;
  Spanska: number;
}



export default function Statistics() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [forumLeaders, setForumLeaders] = useState<ForumLeader[]>([]);
  const [wordSuggestionLeaders, setWordSuggestionLeaders] = useState<ContributionLeader[]>([]);
  const [clueSuggestionLeaders, setClueSuggestionLeaders] = useState<ContributionLeader[]>([]);
  const [recentWords, setRecentWords] = useState<RecentWord[]>([]);
  const [recentClues, setRecentClues] = useState<RecentClue[]>([]);
  const [wordTimeStats, setWordTimeStats] = useState<TimeStats | null>(null);
  const [growthData, setGrowthData] = useState<GrowthDataPoint[]>([]);
  const [searchStats, setSearchStats] = useState<SearchStats | null>(null);
  const [searchGrowthData, setSearchGrowthData] = useState<SearchGrowthPoint[]>([]);
  const [visitorData, setVisitorData] = useState<VisitorDataPoint[]>([]);
  const [dataStartDate, setDataStartDate] = useState<Date | null>(null);
  const [languageGrowthData, setLanguageGrowthData] = useState<LanguageGrowthPoint[]>([]);
  const [clueLanguageGrowthData, setClueLanguageGrowthData] = useState<LanguageGrowthPoint[]>([]);
  const [loading, setLoading] = useState(true);

  usePageVisit('/statistik');
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const now = new Date();
        const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const last7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const last30d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const lastYear = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

        // Run ALL count queries in parallel
        const [
          wordCountResult,
          clueCountResult,
          topicCountResult,
          replyCountResult,
          userCountResult,
          wordLengthStatsResult,
          topicAuthorsResult,
          replyAuthorsResult,
          wordSuggestionsResult,
          clueSuggestionsResult,
          recentWordsResult,
          recentCluesResult,
          words24hResult,
          words7dResult,
          words30dResult,
          wordsYearResult,
          earliestWordResult,
          totalSearchesResult,
          wordSearchTotalResult,
          clueSearchTotalResult,
          searches24hResult,
          searches7dResult,
          searches30dResult,
          // Fetch ALL search logs for growth chart
          allSearchLogsResult,
        ] = await Promise.all([
          supabase.from('words').select('*', { count: 'exact', head: true }),
          supabase.from('clues').select('*', { count: 'exact', head: true }),
          supabase.from('forum_topics').select('*', { count: 'exact', head: true }),
          supabase.from('forum_replies').select('*', { count: 'exact', head: true }),
          supabase.from('profiles').select('*', { count: 'exact', head: true }),
          supabase.rpc('get_word_length_stats'),
          supabase.from('forum_topics').select('author_name').eq('is_deleted', false),
          supabase.from('forum_replies').select('author_name').eq('is_deleted', false),
          supabase.from('word_suggestions').select('user_id').eq('status', 'approved'),
          supabase.from('clue_suggestions').select('user_id').eq('status', 'approved'),
          supabase.from('words').select('id, word, created_at').order('created_at', { ascending: false }).limit(50),
          supabase.from('clues').select('id, clue, created_at, word_id').order('created_at', { ascending: false }).limit(50),
          supabase.from('words').select('*', { count: 'exact', head: true }).gte('created_at', last24h.toISOString()),
          supabase.from('words').select('*', { count: 'exact', head: true }).gte('created_at', last7d.toISOString()),
          supabase.from('words').select('*', { count: 'exact', head: true }).gte('created_at', last30d.toISOString()),
          supabase.from('words').select('*', { count: 'exact', head: true }).gte('created_at', lastYear.toISOString()),
          supabase.from('words').select('created_at').order('created_at', { ascending: true }).limit(1).single(),
          supabase.from('search_logs').select('*', { count: 'exact', head: true }),
          supabase.from('search_logs').select('*', { count: 'exact', head: true }).eq('search_type', 'word'),
          supabase.from('search_logs').select('*', { count: 'exact', head: true }).eq('search_type', 'question'),
          supabase.from('search_logs').select('*', { count: 'exact', head: true }).gte('created_at', last24h.toISOString()),
          supabase.from('search_logs').select('*', { count: 'exact', head: true }).gte('created_at', last7d.toISOString()),
          supabase.from('search_logs').select('*', { count: 'exact', head: true }).gte('created_at', last30d.toISOString()),
          supabase.from('search_logs').select('created_at, search_type').gte('created_at', last30d.toISOString()).limit(50000),
        ]);

        // Process word length distribution from RPC result
        const wordLengthData = wordLengthStatsResult.data || [];
        const wordLengthStats = wordLengthData.map((row: { length: number; count: number }) => ({
          length: `${row.length}`,
          count: Number(row.count),
        }));

        setStats({
          wordCount: wordCountResult.count || 0,
          clueCount: clueCountResult.count || 0,
          topicCount: topicCountResult.count || 0,
          replyCount: replyCountResult.count || 0,
          userCount: userCountResult.count || 0,
          wordLengthStats,
        });

        // Process forum leaderboard
        const topicAuthors = topicAuthorsResult.data || [];
        const replyAuthors = replyAuthorsResult.data || [];
        const authorCounts: Record<string, number> = {};
        topicAuthors.forEach(t => {
          authorCounts[t.author_name] = (authorCounts[t.author_name] || 0) + 1;
        });
        replyAuthors.forEach(r => {
          authorCounts[r.author_name] = (authorCounts[r.author_name] || 0) + 1;
        });
        const sortedLeaders = Object.entries(authorCounts)
          .map(([author_name, post_count]) => ({ author_name, post_count }))
          .sort((a, b) => b.post_count - a.post_count)
          .slice(0, 10);
        setForumLeaders(sortedLeaders);

        // Process word suggestion leaderboard
        const wordSuggestions = wordSuggestionsResult.data || [];
        if (wordSuggestions.length > 0) {
          const userIds = [...new Set(wordSuggestions.map(s => s.user_id).filter(Boolean))] as string[];
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, display_name')
            .in('id', userIds);

          const profileMap = new Map(profiles?.map(p => [p.id, p.display_name]) || []);
          const suggestionCounts: Record<string, { name: string; count: number }> = {};
          
          wordSuggestions.forEach(s => {
            if (s.user_id) {
              const name = profileMap.get(s.user_id) || 'Okänd';
              if (!suggestionCounts[s.user_id]) {
                suggestionCounts[s.user_id] = { name, count: 0 };
              }
              suggestionCounts[s.user_id].count++;
            }
          });

          const sortedWordLeaders = Object.values(suggestionCounts)
            .map(({ name, count }) => ({ display_name: name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
          setWordSuggestionLeaders(sortedWordLeaders);
        }

        // Process clue suggestion leaderboard
        const clueSuggestions = clueSuggestionsResult.data || [];
        if (clueSuggestions.length > 0) {
          const userIds = [...new Set(clueSuggestions.map(s => s.user_id).filter(Boolean))] as string[];
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, display_name')
            .in('id', userIds);

          const profileMap = new Map(profiles?.map(p => [p.id, p.display_name]) || []);
          const suggestionCounts: Record<string, { name: string; count: number }> = {};
          
          clueSuggestions.forEach(s => {
            if (s.user_id) {
              const name = profileMap.get(s.user_id) || 'Okänd';
              if (!suggestionCounts[s.user_id]) {
                suggestionCounts[s.user_id] = { name, count: 0 };
              }
              suggestionCounts[s.user_id].count++;
            }
          });

          const sortedClueLeaders = Object.values(suggestionCounts)
            .map(({ name, count }) => ({ display_name: name, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
          setClueSuggestionLeaders(sortedClueLeaders);
        }

        // Set recent words
        setRecentWords(recentWordsResult.data || []);

        // Process recent clues with word names
        const recentCluesData = recentCluesResult.data || [];
        if (recentCluesData.length > 0) {
          const wordIds = [...new Set(recentCluesData.map(c => c.word_id))];
          const { data: wordsData } = await supabase
            .from('words')
            .select('id, word')
            .in('id', wordIds);

          const wordMap = new Map(wordsData?.map(w => [w.id, w.word]) || []);
          
          setRecentClues(recentCluesData.map(c => ({
            id: c.id,
            clue: c.clue,
            word: wordMap.get(c.word_id) || '',
            created_at: c.created_at || '',
          })));
        }

        // Set time stats
        setWordTimeStats({
          last24h: words24hResult.count || 0,
          last7d: words7dResult.count || 0,
          last30d: words30dResult.count || 0,
          lastYear: wordsYearResult.count || 0,
        });

        // Set data start date
        const startDate = earliestWordResult.data?.created_at 
          ? new Date(earliestWordResult.data.created_at) 
          : new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        setDataStartDate(startDate);

        // Fetch growth data using database function (handles large datasets efficiently)
        const { data: dailyGrowthData } = await supabase.rpc('get_daily_growth_stats', { days_back: 30 });
        
        // Build cumulative growth data (starting from 0)
        const growthPoints: GrowthDataPoint[] = [];
        let runningWords = 0;
        let runningClues = 0;
        
        if (dailyGrowthData) {
          dailyGrowthData.forEach((row: { stat_date: string; word_count: number; clue_count: number }) => {
            runningWords += row.word_count || 0;
            runningClues += row.clue_count || 0;
            
            // stat_date is a date string (YYYY-MM-DD), parse it as local date
            const [year, month, day] = row.stat_date.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            growthPoints.push({
              date: date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }),
              words: runningWords,
              clues: runningClues,
            });
          });
        }
        setGrowthData(growthPoints);

        // Set search stats
        setSearchStats({
          totalSearches: totalSearchesResult.count || 0,
          wordSearches: wordSearchTotalResult.count || 0,
          clueSearches: clueSearchTotalResult.count || 0,
          last24h: searches24hResult.count || 0,
          last7d: searches7dResult.count || 0,
          last30d: searches30dResult.count || 0,
        });

        // Calculate search growth from fetched records
        const allSearchLogs = allSearchLogsResult.data || [];
        const wordSearchesByDate: Record<string, number> = {};
        const clueSearchesByDate: Record<string, number> = {};
        
        allSearchLogs.forEach(s => {
          if (s.created_at) {
            const dateKey = new Date(s.created_at).toISOString().split('T')[0];
            if (s.search_type === 'word') {
              wordSearchesByDate[dateKey] = (wordSearchesByDate[dateKey] || 0) + 1;
            } else if (s.search_type === 'question') {
              clueSearchesByDate[dateKey] = (clueSearchesByDate[dateKey] || 0) + 1;
            }
          }
        });

        const searchGrowthPoints: SearchGrowthPoint[] = [];
        for (let i = 29; i >= 0; i--) {
          const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
          const dateKey = day.toISOString().split('T')[0];
          
          searchGrowthPoints.push({
            date: day.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }),
            wordSearches: wordSearchesByDate[dateKey] || 0,
            clueSearches: clueSearchesByDate[dateKey] || 0,
          });
        }
        setSearchGrowthData(searchGrowthPoints);

        // Fetch visitor data using RPC
        const { data: visitorStatsData } = await supabase.rpc('get_daily_visitor_stats');
        
        if (visitorStatsData) {
          const visitorPoints: VisitorDataPoint[] = visitorStatsData.map((row: {
            stat_date: string;
            visitors: number;
            page_views: number;
          }) => {
            const [year, month, day] = row.stat_date.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return {
              date: date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }),
              visitors: Number(row.visitors) || 0,
              pageViews: Number(row.page_views) || 0,
            };
          });
          setVisitorData(visitorPoints);
        }

        // Fetch language growth data using RPC
        const { data: langGrowthData } = await supabase.rpc('get_language_growth_stats');
        
        if (langGrowthData) {
          const langGrowthPoints: LanguageGrowthPoint[] = langGrowthData.map((row: { 
            stat_date: string; 
            sv_count: number; 
            en_count: number; 
            de_count: number; 
            fr_count: number; 
            es_count: number; 
          }) => {
            const [year, month, day] = row.stat_date.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return {
              date: date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }),
              Svenska: Number(row.sv_count) || 0,
              Engelska: Number(row.en_count) || 0,
              Tyska: Number(row.de_count) || 0,
              Franska: Number(row.fr_count) || 0,
              Spanska: Number(row.es_count) || 0,
            };
          });
          setLanguageGrowthData(langGrowthPoints);
        }

        // Fetch clue language growth data using RPC
        const { data: clueLangGrowthData } = await supabase.rpc('get_clue_language_growth_stats');
        
        if (clueLangGrowthData) {
          const clueLangGrowthPoints: LanguageGrowthPoint[] = clueLangGrowthData.map((row: { 
            stat_date: string; 
            sv_count: number; 
            en_count: number; 
            de_count: number; 
            fr_count: number; 
            es_count: number; 
          }) => {
            const [year, month, day] = row.stat_date.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return {
              date: date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }),
              Svenska: Number(row.sv_count) || 0,
              Engelska: Number(row.en_count) || 0,
              Tyska: Number(row.de_count) || 0,
              Franska: Number(row.fr_count) || 0,
              Spanska: Number(row.es_count) || 0,
            };
          });
          setClueLanguageGrowthData(clueLangGrowthPoints);
        }

      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const StatCard = ({ title, value, icon: Icon, description }: { title: string; value: number; icon: typeof BookOpen; description: string }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString('sv-SE')}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('sv-SE', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-muted-foreground">Laddar statistik...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Statistik</h1>

        {/* Overview Stats */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-8">
          <StatCard
            title="Ord"
            value={stats?.wordCount || 0}
            icon={BookOpen}
            description="Totalt antal ord i databasen"
          />
          <StatCard
            title="Ledtrådar"
            value={stats?.clueCount || 0}
            icon={FileText}
            description="Totalt antal ledtrådar"
          />
          <StatCard
            title="Forumämnen"
            value={stats?.topicCount || 0}
            icon={MessageSquare}
            description="Antal diskussioner"
          />
          <StatCard
            title="Svar"
            value={stats?.replyCount || 0}
            icon={MessageSquare}
            description="Antal forumsvar"
          />
          <StatCard
            title="Användare"
            value={stats?.userCount || 0}
            icon={Users}
            description="Registrerade användare"
          />
        </div>

        {/* Time-based word stats */}
        {wordTimeStats && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Nya ord över tid
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{wordTimeStats.last24h}</div>
                  <div className="text-sm text-muted-foreground">Senaste 24h</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{wordTimeStats.last7d}</div>
                  <div className="text-sm text-muted-foreground">Senaste 7 dagar</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{wordTimeStats.last30d}</div>
                  <div className="text-sm text-muted-foreground">Senaste 30 dagar</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{wordTimeStats.lastYear}</div>
                  <div className="text-sm text-muted-foreground">Senaste året</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Statistics */}
        {searchStats && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Sökstatistik
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3 mb-6">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{searchStats.totalSearches.toLocaleString('sv-SE')}</div>
                  <div className="text-sm text-muted-foreground">Totala sökningar</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{searchStats.wordSearches.toLocaleString('sv-SE')}</div>
                  <div className="text-sm text-muted-foreground">Ordsökningar</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{searchStats.clueSearches.toLocaleString('sv-SE')}</div>
                  <div className="text-sm text-muted-foreground">Ledtrådssökningar</div>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-xl font-semibold">{searchStats.last24h}</div>
                  <div className="text-sm text-muted-foreground">Senaste 24h</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-xl font-semibold">{searchStats.last7d}</div>
                  <div className="text-sm text-muted-foreground">Senaste 7 dagar</div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-xl font-semibold">{searchStats.last30d}</div>
                  <div className="text-sm text-muted-foreground">Senaste 30 dagar</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Growth Chart */}
        {searchGrowthData.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Sökningar per dag (senaste 30 dagarna)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={searchGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="wordSearches" stroke="hsl(var(--primary))" name="Ordsökningar" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="clueSearches" stroke="hsl(var(--secondary))" name="Ledtrådssökningar" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Visitor Chart */}
        {visitorData.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Besökare per dag (senaste 30 dagarna)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3 mb-6">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{visitorData.reduce((sum, d) => sum + d.visitors, 0).toLocaleString('sv-SE')}</div>
                  <div className="text-sm text-muted-foreground">Unika besökare</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{visitorData.reduce((sum, d) => sum + (d.pageViews || 0), 0).toLocaleString('sv-SE')}</div>
                  <div className="text-sm text-muted-foreground">Sidvisningar</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{visitorData[visitorData.length - 1]?.visitors || 0}</div>
                  <div className="text-sm text-muted-foreground">Besökare idag</div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={visitorData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="visitors" stroke="hsl(var(--primary))" name="Unika besökare" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="pageViews" stroke="hsl(var(--secondary))" name="Sidvisningar" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Leaderboards */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          {/* Word Suggestion Leaders */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Topplista ordförslag
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {wordSuggestionLeaders.length > 0 ? (
                  <div className="space-y-2">
                    {wordSuggestionLeaders.map((leader, index) => (
                      <div key={leader.display_name} className="flex items-center justify-between p-2 rounded-lg bg-muted">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-muted-foreground w-6">{index + 1}.</span>
                          <span>{leader.display_name}</span>
                        </div>
                        <span className="font-semibold">{leader.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">Inga godkända ordförslag än</div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Clue Suggestion Leaders */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Topplista ledtrådsförslag
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {clueSuggestionLeaders.length > 0 ? (
                  <div className="space-y-2">
                    {clueSuggestionLeaders.map((leader, index) => (
                      <div key={leader.display_name} className="flex items-center justify-between p-2 rounded-lg bg-muted">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-muted-foreground w-6">{index + 1}.</span>
                          <span>{leader.display_name}</span>
                        </div>
                        <span className="font-semibold">{leader.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">Inga godkända ledtrådsförslag än</div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Forum Leaders */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Topplista forum
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                {forumLeaders.length > 0 ? (
                  <div className="space-y-2">
                    {forumLeaders.map((leader, index) => (
                      <div key={leader.author_name} className="flex items-center justify-between p-2 rounded-lg bg-muted">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-muted-foreground w-6">{index + 1}.</span>
                          <span>{leader.author_name}</span>
                        </div>
                        <span className="font-semibold">{leader.post_count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">Inga foruminlägg än</div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Recent Content */}
        <div className="grid gap-4 md:grid-cols-2 mb-8">
          {/* Recent Words */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Senaste 50 orden
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-80">
                <div className="space-y-2">
                  {recentWords.map((word) => (
                    <div key={word.id} className="flex items-center justify-between p-2 rounded-lg border">
                      <span className="font-medium">{capitalizeFirst(word.word)}</span>
                      <span className="text-sm text-muted-foreground">{formatDate(word.created_at)}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Recent Clues */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Senaste 50 ledtrådarna
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-80">
                <div className="space-y-2">
                  {recentClues.map((clue) => (
                    <div key={clue.id} className="flex flex-col p-2 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{capitalizeFirst(clue.word)}</span>
                        <span className="text-sm text-muted-foreground">{formatDate(clue.created_at)}</span>
                      </div>
                      <span className="text-sm text-muted-foreground truncate">{clue.clue}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Language Growth Chart */}
        {languageGrowthData.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Tillväxt ord per språk (sedan 2 jan 2026)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={languageGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="Svenska" stroke="hsl(var(--primary))" name="Svenska" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Engelska" stroke="#ef4444" name="Engelska" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Tyska" stroke="#22c55e" name="Tyska" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Franska" stroke="#3b82f6" name="Franska" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Spanska" stroke="#eab308" name="Spanska" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Clue Language Growth Chart */}
        {clueLanguageGrowthData.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Tillväxt ledtrådar per språk (sedan 2 jan 2026)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={clueLanguageGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="Svenska" stroke="hsl(var(--primary))" name="Svenska" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Engelska" stroke="#ef4444" name="Engelska" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Tyska" stroke="#22c55e" name="Tyska" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Franska" stroke="#3b82f6" name="Franska" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Spanska" stroke="#eab308" name="Spanska" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Word Length Distribution */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Ordlängdsfördelning</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats?.wordLengthStats || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="length" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Version info */}
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-sm text-muted-foreground">
              <p>Version {getCurrentVersion()}</p>
              <p>Byggd: {new Date().toLocaleDateString('sv-SE')}</p>
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
